import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useKnowledge, useWork } from "../api/queries";
import { PAGES, buildCommands, filterCommands } from "../commands";

/** Ouvre la palette depuis un bouton (barre latérale) : même effet que Ctrl+K. */
export const openPalette = () => window.dispatchEvent(new Event("pb:palette"));

const typing = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  return !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
};

/**
 * Palette de commandes (PB-125) : Ctrl+K (Cmd+K) pour aller à une page, ouvrir une tâche ou une fiche par sa clé ou son
 * titre, ou lancer une action ; « g » puis une lettre pour aller à une page ; « ? » pour l'aide. Combobox ARIA : les
 * flèches parcourent la liste, Entrée ouvre, Échap ferme.
 */
export function CommandPalette({ tenant, project, org }: { tenant: string; project: string; org: boolean }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [help, setHelp] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const opener = useRef<Element | null>(null);
  const scope = { tenant, project };
  const work = useWork(scope);
  const knowledge = useKnowledge(scope);

  const commands = useMemo(
    () =>
      buildCommands(
        project,
        org ? [] : (work.data ?? []),
        (knowledge.data?.items ?? []).map((i) => ({ key: i.key ?? i.item_id, title: i.title, type: i.type })),
        org,
      ),
    [project, org, work.data, knowledge.data],
  );
  const shown = filterCommands(commands, query);

  const show = () => {
    opener.current = document.activeElement;
    setQuery("");
    setActive(0);
    setHelp(false);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setHelp(false);
    (opener.current as HTMLElement | null)?.focus?.();
  };
  const run = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  useEffect(() => {
    let pendingG = 0;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        show();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey || typing(e.target) || document.querySelector("[aria-modal='true']")) return;
      if (e.key === "?") {
        e.preventDefault();
        opener.current = document.activeElement;
        setHelp(true);
        setOpen(true);
        return;
      }
      if (e.key === "g") {
        pendingG = Date.now();
        return;
      }
      if (pendingG && Date.now() - pendingG < 1200) {
        pendingG = 0;
        const page = PAGES.find((p) => p.shortcut === e.key && (!org || !["today", "board", "live"].includes(p.section)));
        if (page) {
          e.preventDefault();
          navigate(`/p/${project}/${page.section}`);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pb:palette", show);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pb:palette", show);
    };
    // show et navigate ne changent pas de comportement d'un rendu à l'autre : seuls le projet et sa nature comptent.
  }, [project, org]);

  useEffect(() => {
    if (open && !help) input.current?.focus();
  }, [open, help]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div
        className="modal card palette"
        role="dialog"
        aria-modal="true"
        aria-label={help ? "Raccourcis clavier" : "Palette de commandes"}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            close();
          }
        }}
      >
        {help ? (
          <div className="stack" style={{ gap: 10 }}>
            <h2 style={{ margin: 0 }}>Raccourcis clavier</h2>
            <dl className="facts">
              <dt>
                <kbd>Ctrl</kbd> <kbd>K</kbd>
              </dt>
              <dd>Palette : aller à une page, ouvrir une tâche ou une fiche, lancer une action</dd>
              <dt>
                <kbd>/</kbd>
              </dt>
              <dd>Rechercher dans la mémoire</dd>
              {PAGES.filter((p) => !org || !["today", "board", "live"].includes(p.section)).map((p) => (
                <span key={p.section} style={{ display: "contents" }}>
                  <dt>
                    <kbd>g</kbd> <kbd>{p.shortcut}</kbd>
                  </dt>
                  <dd>{p.label}</dd>
                </span>
              ))}
              <dt>
                <kbd>?</kbd>
              </dt>
              <dd>Cette aide</dd>
            </dl>
            <div className="row">
              <span className="spacer" />
              <button className="small" onClick={close} autoFocus>
                Fermer
              </button>
            </div>
          </div>
        ) : (
          <>
            <input
              ref={input}
              role="combobox"
              aria-expanded="true"
              aria-controls="palette-list"
              aria-activedescendant={shown[active] ? `palette-${active}` : undefined}
              aria-autocomplete="list"
              aria-label="Commande, tâche ou fiche"
              placeholder="Aller à, ouvrir une tâche ou une fiche (clé ou titre)…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActive((a) => Math.min(a + 1, shown.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActive((a) => Math.max(a - 1, 0));
                } else if (e.key === "Enter" && shown[active]) {
                  e.preventDefault();
                  run(shown[active].to);
                }
              }}
            />
            <ul id="palette-list" role="listbox" aria-label="Résultats" className="palette-list">
              {shown.length === 0 && <li className="small muted palette-empty">Rien ne correspond.</li>}
              {shown.map((c, i) => (
                <li
                  key={c.id}
                  id={`palette-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className={i === active ? "active" : ""}
                  onMouseMove={() => setActive(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    run(c.to);
                  }}
                >
                  <span className="small muted palette-group">{c.group}</span>
                  <span className="palette-label">{c.label}</span>
                  {c.hint && <span className="small faint">{c.hint}</span>}
                </li>
              ))}
            </ul>
            <div className="small faint">
              <kbd>↑</kbd> <kbd>↓</kbd> parcourir · <kbd>Entrée</kbd> ouvrir · <kbd>Échap</kbd> fermer · <kbd>?</kbd> raccourcis
            </div>
          </>
        )}
      </div>
    </div>
  );
}
